// Generic, dependency-free scheduler: caps how many callers can hold a "slot"
// at once, and lets keys tagged "priority" cut in front of "standard" keys for
// the next slot that frees up, without aborting anything already running.

export type LoadPriority = "standard" | "priority";

interface Waiter {
  key: string;
  priority?: LoadPriority; // Set for one-off requests that never go through setPriority.
  resolve: () => void;
}

export class ApiLoadScheduler {
  private activeSlots = 0;
  private waiters: Waiter[] = [];
  private itemPriority = new Map<string, LoadPriority>();

  // Visible bookkeeping: keys requested but not yet started.
  standardQueue: string[] = [];
  priorityQueue: string[] = [];

  constructor(private concurrency = 5) {}

  // Called when a key is first requested (or re-requested at a higher priority).
  setPriority(key: string, priority: LoadPriority) {
    if (priority === "standard" && this.itemPriority.get(key) === "priority") {
      return; // never downgrade something already promoted to priority
    }
    this.itemPriority.set(key, priority);
    this.standardQueue = this.standardQueue.filter(k => k !== key);
    this.priorityQueue = this.priorityQueue.filter(k => k !== key);
    (priority === "priority" ? this.priorityQueue : this.standardQueue).push(key);
  }

  // Called once actual work starts for a key: it's "running", not "queued".
  markStarted(key: string) {
    this.standardQueue = this.standardQueue.filter(k => k !== key);
    this.priorityQueue = this.priorityQueue.filter(k => k !== key);
  }

  // Acquire one of the global slots. Resolves to a release() callback. `key` must be
  // the same key passed to setPriority, or pass `priority` directly for one-off
  // requests that don't have one.
  acquire(key: string, priority?: LoadPriority): Promise<() => void> {
    if (this.activeSlots < this.concurrency) {
      this.activeSlots++;
      return Promise.resolve(() => this.release());
    }
    return new Promise<() => void>(resolve => {
      this.waiters.push({
        key,
        priority,
        resolve: () => {
          this.activeSlots++;
          resolve(() => this.release());
        }
      });
    });
  }

  private release() {
    this.activeSlots--;
    if (this.waiters.length === 0) {
      return;
    }
    // Prefer a waiter whose key is *currently* marked priority (checked live,
    // not at the time it started waiting) - so a key promoted to priority while
    // already queued for a slot jumps ahead of standard-queue waiters.
    let idx = this.waiters.findIndex(w => w.priority === "priority" || this.itemPriority.get(w.key) === "priority");
    if (idx === -1) {
      idx = 0;
    }
    const [waiter] = this.waiters.splice(idx, 1);
    waiter.resolve();
  }
}
